/**
 * Main Action Screen - 30s and 60s replay buttons
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Clock, Zap, Send, CheckCircle2, 
  MapPin, ChevronRight, DoorOpen 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { WeatherWidget } from '../components/WeatherWidget';
import { Navbar } from '../components/Navbar';
import { clipsAPI, courtAPI, authAPI } from '../services/api';
import { useAuthStore, useSessionStore, useLocationStore } from '../store';
import { toast } from 'sonner';
import { getRandomPhrase } from '../utils/replayPhrases';

export default function ActionScreen() {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuthStore();
  const { club, court, expiresAt, clearSession } = useSessionStore();
  const { userLocation } = useLocationStore();
  
  const [requestSent, setRequestSent] = useState(null); // 'normal' | 'fast' | null
  const [sentPhrase, setSentPhrase] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0); // Cooldown timer
  
  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);
  
  // Fetch session info on mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await courtAPI.getSession();
        setSessionInfo(response.data);
        
        // Don't redirect, just show no-session state
        if (!response.data.active) {
          setSessionInfo({ active: false });
        }
      } catch (err) {
        console.error('Session fetch error:', err);
        // For demo mode without auth, allow access
        setSessionInfo({ active: false });
      }
    };
    
    fetchSession();
  }, [navigate]);
  
  // Clear confirmation after 5 seconds
  useEffect(() => {
    if (requestSent) {
      const timer = setTimeout(() => setRequestSent(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [requestSent]);
  
  const handleRequestClip = async (isFast = false) => {
    // Check if in cooldown
    if (cooldownSeconds > 0) {
      toast.error(`Espera ${cooldownSeconds} segundos antes de solicitar otro replay`);
      return;
    }
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 50]);
    }
    
    // Show immediate confirmation with random phrase
    setRequestSent(isFast ? 'fast' : 'normal');
    setSentPhrase(getRandomPhrase());
    
    // Duration: Normal = 30s, Fast = 60s (will become 30s at 2x)
    const captureDuration = isFast ? 60 : 30;
    
    try {
      await clipsAPI.requestClip({
        session_id: sessionInfo?.court_id || court?.id || 'demo',
        duration: captureDuration,
        is_fast: isFast,
      });
      
      // Start cooldown timer (15 seconds)
      setCooldownSeconds(15);
      
      // Success toast with navigation hint
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">¡Solicitud enviada!</span>
          <span className="text-sm opacity-80">
            Tu replay estará listo en &quot;Mis Replays&quot; en menos de 5 min
          </span>
        </div>,
        { duration: 4000 }
      );
      
    } catch (err) {
      setRequestSent(null);
      const errorResponse = err.response;
      
      // Handle rate limit error (429)
      if (errorResponse?.status === 429) {
        // Extract seconds from error message
        const detail = errorResponse?.data?.detail || '';
        const match = detail.match(/(\d+) segundos/);
        if (match) {
          setCooldownSeconds(parseInt(match[1]));
        } else {
          setCooldownSeconds(15);
        }
        toast.error(detail || 'Espera antes de solicitar otro replay');
      } else {
        const message = errorResponse?.data?.detail || 'Error solicitando replay';
        toast.error(message);
      }
    }
  };
  
  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    clearSession();
    authLogout();
    toast.success('Sesión cerrada');
    navigate('/');
  };
  
  const handleLeaveCourt = async () => {
    try {
      await courtAPI.leaveCourt();
      clearSession();
      toast.success('Saliste de la cancha');
      navigate('/scan');
    } catch (err) {
      console.error('Leave court error:', err);
      toast.error('Error al salir de la cancha');
    }
  };
  
  // Get location for weather
  const weatherLat = userLocation?.lat || club?.location?.lat || 19.4326;
  const weatherLng = userLocation?.lng || club?.location?.lng || -99.1332;
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <Navbar />
      
      {/* Weather widget with club/court info integrated */}
      <div className="px-4 py-3">
        <WeatherWidget 
          lat={weatherLat} 
          lng={weatherLng} 
          clubName={club?.name || 'Demo Club'}
          courtNumber={court?.court_number || 1}
        />
      </div>
      
      {/* Main action buttons */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        {/* Cooldown indicator */}
        {cooldownSeconds > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-4 py-2 flex items-center gap-2"
          >
            <Clock className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-yellow-500">
              Espera {cooldownSeconds}s para otro replay
            </span>
          </motion.div>
        )}
        
        {/* 30s Normal button */}
        <motion.button
          whileTap={{ scale: cooldownSeconds > 0 ? 1 : 0.95 }}
          onClick={() => handleRequestClip(false)}
          disabled={requestSent === 'normal' || cooldownSeconds > 0}
          className={`action-button w-full max-w-sm h-32 rounded-2xl bg-card border-2 flex flex-col items-center justify-center gap-2 transition-all ${
            cooldownSeconds > 0 
              ? 'border-muted/30 opacity-50 cursor-not-allowed' 
              : 'border-primary/20 disabled:opacity-70'
          }`}
          data-testid="replay-30s-btn"
        >
          <AnimatePresence mode="wait">
            {requestSent === 'normal' ? (
              <motion.div
                key="sent"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Send className="w-6 h-6 text-green-500" />
                </div>
                <span className="text-sm text-green-500 font-medium text-center px-4">{sentPhrase}</span>
              </motion.div>
            ) : (
              <motion.div
                key="normal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-8 h-8 text-primary" />
                  <span className="font-display font-black text-4xl tracking-tighter">30s</span>
                </div>
                <span className="text-sm text-muted-foreground uppercase tracking-wider">
                  Replay Normal
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
        
        {/* 60s Fast button (outputs 30s at 2x speed) */}
        <motion.button
          whileTap={{ scale: cooldownSeconds > 0 ? 1 : 0.95 }}
          onClick={() => handleRequestClip(true)}
          disabled={requestSent === 'fast' || cooldownSeconds > 0}
          className={`action-button w-full max-w-sm h-32 rounded-2xl bg-primary/5 border-2 flex flex-col items-center justify-center gap-2 transition-all ${
            cooldownSeconds > 0 
              ? 'border-muted/30 opacity-50 cursor-not-allowed' 
              : 'border-primary/30 disabled:opacity-70 glow-primary'
          }`}
          data-testid="replay-60s-btn"
        >
          <AnimatePresence mode="wait">
            {requestSent === 'fast' ? (
              <motion.div
                key="sent"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Send className="w-6 h-6 text-green-500" />
                </div>
                <span className="text-sm text-green-500 font-medium text-center px-4">{sentPhrase}</span>
              </motion.div>
            ) : (
              <motion.div
                key="fast"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-8 h-8 text-primary" />
                  <span className="font-display font-black text-4xl tracking-tighter text-primary">60s</span>
                </div>
                <span className="text-sm text-primary/80 uppercase tracking-wider">
                  Replay Rápido 2x → 30s
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
      
      {/* Session info footer */}
      <div className="p-4 border-t border-border space-y-3">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{club?.city || 'México'}</span>
          </div>
          <span>
            Cancha activa hasta{' '}
            {expiresAt ? new Date(expiresAt).toLocaleTimeString('es-MX', { 
              hour: '2-digit', 
              minute: '2-digit' 
            }) : '—'}
          </span>
        </div>
        
        {/* Leave court button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={handleLeaveCourt}
          data-testid="leave-court-btn"
        >
          <DoorOpen className="w-4 h-4 mr-2" />
          Salir de esta cancha
        </Button>
      </div>
    </div>
  );
}
